/* eslint-disable @next/next/no-img-element */
import React, { useEffect, useState } from 'react'
import Cookies from 'js-cookie'
import Image from 'next/image'
import Button from '@/src/components/button'
import {
  Box,
  CircularProgress,
  Divider,
  Modal,
  Skeleton,
  TextField,
  Typography
} from '@mui/material'
import LoginForm, { type Props as LoginFormProps } from '@/src/components/login-form/LoginForm'
import { useRouter } from 'next/router'
import { LoginImage } from '@/src/assets/images'
import CommonApi from '@/pages/api'
import Link from 'next/link'
import { handleSessionVisits } from '@/src/types/helper'
import { useStore } from '@/src/types/store'
import BlockUserPage from '@/src/components/signin-blocked'
import { useTier } from '@/src/hooks/useTier'
import TwoFactorForm from '@/src/components/two-factor/TwoFactorForm'
import TotpSetupWizard from '@/src/components/two-factor/TotpSetupWizard'
import {
  isSessionExpired,
  type LoginResponse,
  type LoginStep
} from '@/src/components/two-factor/loginFlow'
import {
  clearLoginChallenge,
  loadLoginChallenge,
  saveLoginChallenge,
  type LoginChallenge
} from '@/src/components/two-factor/twoFactorSession'
import { TwoFactorEndpoints } from '@/pages/api/endpoints'
import dayjs from 'dayjs'
import { validatePath } from '@/src/common'
const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 400,
  bgcolor: 'background.paper',
  borderRadius: 2,
  boxShadow: 24,
  p: 4
}

const Login = () => {
  const [verificationModal, setVerificationModal] = useState(false)
  const [loading, setLoading] = useState(false)
  const [isVerified, setIsVerified] = useState(false)
  const [verificationCode, setVerificationCode] = useState('')
  const [userName, setUserName] = useState('')
  const [blockedUser, setBlockedUser] = useState(null)
  const [step, setStep] = useState<LoginStep>('credentials')
  const [challenge, setChallenge] = useState<LoginChallenge | null>(null)
  const [setupToken, setSetupToken] = useState('')

  const router = useRouter()
  const customRedirectUrl: any = router?.query?.redirect_url ?? null
  const fullUrl = router.asPath
  let redirectUrl = '/'
  if (fullUrl.includes('redirect_url=')) {
    const redirectPart = fullUrl.split('redirect_url=')[1]
    redirectUrl = decodeURIComponent(redirectPart)
  }
  const safeRedirectUrl = validatePath(customRedirectUrl?.split('?')[0]) ? redirectUrl : '/'
  const { redirectionUrl, themeLogo, themeLoading, setProfile, openToast } = useStore()
  const [isPathPortal, setIsPathPortal] = useState(false)
  // Investor accounts only exist on 360. On the other tiers a new account
  // could be created but never signed in with, so the Register link goes.
  const { canInvestorLogin } = useTier()

  useEffect(() => {
    if (typeof window !== 'undefined') {
      setIsPathPortal(window.location.hostname.startsWith('portal'))
    }
  }, [])

  // Everything that used to run inline once `login` answered with a token. The
  // 2FA steps finish on the same code path once their own call returns it.
  const completeLogin = (response: LoginResponse) => {
    // Never write the cookie without a token — it would land as the string
    // "undefined" and every later request would go out with a bad Bearer.
    if (!response?.access_token) {
      openToast(response?.msg || 'Something went wrong. Please sign in again.', 'error')
      backToCredentials()
      return
    }
    openToast('Login successfully!', 'success')
    Cookies.set('access_token', response.access_token)
    // If opened by widget popup, send token back and close
    if (customRedirectUrl === '__widget_login__' && window.opener) {
      window.opener.postMessage(
        { type: 'RELAIT_WIDGET_LOGIN', access_token: response.access_token },
        '*'
      )
      window.close()
      return
    }

    if (response?.data?.user_type === 'investor') {
      handleSessionVisits(false, true)
    }

    if (redirectionUrl) {
      router.push(redirectionUrl)
    } else {
      if (response?.data?.user_type === 'investor') {
        router.push('/')
      } else {
        if (customRedirectUrl && customRedirectUrl !== '__widget_login__') {
          router.push(`${safeRedirectUrl}`)
        } else {
          router.push('/admin')
        }
      }
    }
  }

  const backToCredentials = () => {
    clearLoginChallenge()
    setChallenge(null)
    setSetupToken('')
    setStep('credentials')
  }

  // A refresh mid-step resumes the code box while the challenge is still valid
  useEffect(() => {
    const saved = loadLoginChallenge()
    if (saved) {
      setChallenge(saved)
      setUserName(saved.email)
      setStep('code')
    }
  }, [])

  const handleFormValues: LoginFormProps['onSubmit'] = async values => {
    setProfile((prev: any) => ({ ...prev, user_type: '' }))
    setUserName(values?.email)
    const authenticationData = {
      email: values?.email,
      password: values?.password
    }
    try {
      setLoading(true)

      const response = await CommonApi({
        path: 'login',
        method: 'post',
        data: authenticationData
      })

      // 2FA answers come back with status:true but no access_token — check them
      // before the cookie write, otherwise the cookie becomes "undefined".
      if (response?.two_factor_required) {
        const expiresIn = Number(response?.expires_in) || 180
        const nextChallenge: LoginChallenge = {
          challenge_id: response?.challenge_id,
          email: values?.email,
          email_hint: response?.email_hint,
          expires_at: Date.now() + expiresIn * 1000
        }
        saveLoginChallenge(nextChallenge)
        setChallenge(nextChallenge)
        setStep('code')
        return
      }

      if (response?.two_factor_setup_required) {
        // setup_token lives in state only — never a cookie, never localStorage
        setSetupToken(response?.setup_token)
        setStep('setup')
        return
      }

      if (response.status) {
        completeLogin(response)
      }
    } catch (error: any) {
      if (error?.response?.data?.msg == 'User is not confirmed.') {
        setVerificationModal(true)
      }
      if (error?.response?.data?.blocked) {
        setBlockedUser(error?.response?.data)
      }
      openToast(error?.response?.data?.msg || 'Invalid business user!', 'error')
    } finally {
      setLoading(false)
    }
  }

  const handleVerifyCode = async (code: string) => {
    if (!challenge) return
    try {
      setLoading(true)
      const response = await CommonApi({
        path: TwoFactorEndpoints.loginVerify,
        method: 'post',
        data: {
          email: challenge.email,
          challenge_id: challenge.challenge_id,
          code
        }
      })

      if (response?.status && response?.access_token) {
        clearLoginChallenge()
        setChallenge(null)
        completeLogin(response)
        return
      }
      openToast(response?.msg || 'Invalid verification code. Please try again.', 'error')
    } catch (error: any) {
      const msg = error?.response?.data?.msg
      openToast(msg || 'Invalid verification code. Please try again.', 'error')
      // an expired challenge cannot be retried — send the user back to the password
      if (isSessionExpired(error)) {
        backToCredentials()
      }
    } finally {
      setLoading(false)
    }
  }

  const handleVerifiyUser = async () => {
    try {
      setLoading(true)
      const response = await CommonApi({
        path: 'confirm-signup',
        method: 'post',
        data: {
          email: userName,
          confirmation_code: verificationCode
        }
      })
      if (response.status) {
        setIsVerified(true)
        openToast(response?.msg ?? 'Verified successfully!', 'success')
        router.push('/login')
        setVerificationModal(false)
      }
    } catch (error: any) {
      openToast(error?.response?.data?.msg || 'Invalid code!', 'error')
    } finally {
      setLoading(false)
    }
  }

  const handleResendVerificationCode = async () => {
    try {
      const response = await CommonApi({
        path: 'resend-confirmation-code',
        method: 'post',
        data: {
          email: userName
        }
      })
      if (response.status) {
        openToast('Confirmation code sent successfully!', 'success')
      }
    } catch (error: any) {
      openToast(error?.response?.data?.msg || 'Something went wrong', 'error')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div>
      <div className="flex w-full h-[100dvh] justify-between">
        <div className="w-full md:w-[50vw] flex justify-center pt-8 pb-16 md:py-0 md:items-center relative">
          <div className="flex flex-col my-auto md:my-0 relative ">
            <div className="w-full flex justify-center">
              {themeLoading ? (
                <Skeleton className="h-[4.75rem] w-[10rem]" />
              ) : !window.location.hostname.startsWith('portal') ? (
                <Link href="/">
                  <img src={themeLogo.logo} alt="Logo" className="max-w-[20.75rem] h-[2.75rem] object-contain" />
                </Link>
              ) : (
                <img src={themeLogo.logo} alt="Logo" className="max-w-[20.75rem] h-[2.75rem] object-contain" />
              )}
            </div>
            {blockedUser ? (
              <BlockUserPage
                handleBack={setBlockedUser}
                blockedUserData={blockedUser}
                loading={loading}
              />
            ) : step === 'code' && challenge ? (
              <div className="pt-8 md:pt-16">
                <TwoFactorForm
                  key={challenge.challenge_id}
                  loading={loading}
                  emailHint={challenge.email_hint}
                  expiresIn={Math.max(0, Math.round((challenge.expires_at - Date.now()) / 1000))}
                  onVerify={handleVerifyCode}
                  onBack={backToCredentials}
                />
              </div>
            ) : step === 'setup' ? (
              <div className="pt-8 md:pt-16">
                <TotpSetupWizard
                  mode="login"
                  setupToken={setupToken}
                  compact={customRedirectUrl === '__widget_login__'}
                  onComplete={completeLogin}
                  onCancel={backToCredentials}
                />
              </div>
            ) : (
              <div>
                <Typography variant="h1" className="pb-8 pt-8 md:pt-16 text-center">
                  Sign In
                </Typography>
                <LoginForm loading={loading} onSubmit={handleFormValues} isDisable={false} />
                <Box className="flex gap-2 w-full pt-6 justify-center sm:justify-start relative items-center">
                  {!isPathPortal && canInvestorLogin && (
                    <>
                      <Typography variant="body2" color="text.secondary">
                        {"Don't have an account?"}
                      </Typography>
                      <Link
                        href={{
                          pathname: '/create-account',
                          query: { redirect_url: '__widget_login__' }
                        }}
                      >
                        <Typography variant="buttonLink" color="primary.main">
                          Register
                        </Typography>
                      </Link>
                    </>
                  )}
                </Box>
              </div>
            )}
          </div>
          <div className="text-secondary-main w-full pl-4 flex justify-start absolute gap-4 bottom-4 left-0 md:left-8">
            <Typography variant="caption">© {dayjs().format('YYYY')} Relait PTY LTD</Typography>
            {'  '}
            <div className="flex items-center gap-2">
              <Typography variant="buttonLarge_sm" color="text.secondary">
                <a
                  href="https://www.relait.com.au/privacy-policy"
                  target="_blank"
                  className="hover:underline"
                >
                  Privacy
                </a>
              </Typography>
              <Divider
                orientation="vertical"
                className="h-3 w-px items-center flex justify-center"
              />
              <Typography variant="buttonLarge_sm" color="text.secondary">
                <Link
                  href="https://www.relait.com.au/eula"
                  target="_blank"
                  className="hover:underline"
                >
                  Terms
                </Link>
              </Typography>
            </div>
          </div>
        </div>
        <div className="hidden md:flex justify-end w-[50vw] h-full">
          <Image src={LoginImage} className="h-[100vh]  aspect-auto " alt="login" />
        </div>
      </div>

      {/* Verification Modal */}

      <Modal open={verificationModal}>
        <Box sx={style}>
          {loading && (
            <div className="w-full flex flex-col justify-center items-center">
              <CircularProgress />
              <Typography>Processing</Typography>
            </div>
          )}
          {!loading && (
            <div>
              {isVerified ? (
                <Typography> Registered Successfully!</Typography>
              ) : (
                <>
                  <Typography>
                    {' '}
                    You are un verified user, check you email and enter your verification code.
                  </Typography>

                  <TextField
                    label="Verification Code"
                    fullWidth
                    margin="normal"
                    onChange={e => setVerificationCode(e.target.value)}
                  />
                  <div className="flex gap-6 justify-center mt-4">
                    <Button
                      variant="outlined"
                      onClick={handleResendVerificationCode}
                      text="Resend"
                    />

                    <Button
                      // className="bg-blue-500"
                      variant="contained"
                      // disableElevation
                      onClick={handleVerifiyUser}
                      text="Confirm"
                    />
                  </div>
                </>
              )}
            </div>
          )}
        </Box>
      </Modal>
    </div>
  )
}

export default Login
